import {
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { settingsCopy } from '@/copy'
import type {
  ProviderFormBody,
  ProviderUpdateBody,
} from '../components/ProviderList'
import { useToastStore } from '../store/toast'
import type { SystemProviderInfo } from '../types/api'

/** Adapter so admin and workspace callers can pass a uniform API shape. */
interface ProviderCrudApi {
  list: () => Promise<SystemProviderInfo[]>
  create: (body: ProviderFormBody) => Promise<unknown>
  update: (id: string, body: ProviderUpdateBody) => Promise<unknown>
  delete: (id: string) => Promise<unknown>
}

export interface UseProviderCrudOptions {
  queryKey: QueryKey
  api: ProviderCrudApi
  /** Extra query keys to invalidate on success (e.g. dropdown caches). */
  invalidateKeys?: QueryKey[]
  enabled?: boolean
  staleTime?: number
}

/** Extract a useful message from a mutation error, falling back to copy. */
function mutationError(
  isError: boolean,
  error: unknown,
  fallback: string,
): string | undefined {
  if (!isError) return undefined
  return error instanceof Error ? error.message : fallback
}

/**
 * Shared create/update/delete wiring for provider lists.
 *
 * Returns props that spread directly onto `<ProviderList>`:
 * `providers`, `isLoading`, `isError/onRetry`, `onCreate/isCreating/createError`,
 * `onUpdate/isUpdating/updateError`, `onDelete/isDeleting`.
 *
 * Error messages use `settingsCopy.providers.*` with `error.message` extraction
 * for consistency between admin and workspace views.
 */
export function useProviderCrud(options: UseProviderCrudOptions) {
  const qc = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)

  const {
    data: providers = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<SystemProviderInfo[]>({
    queryKey: options.queryKey,
    queryFn: options.api.list,
    enabled: options.enabled,
    staleTime: options.staleTime,
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: options.queryKey })
    options.invalidateKeys?.forEach(
      (key) => void qc.invalidateQueries({ queryKey: key }),
    )
  }

  const createMutation = useMutation({
    mutationFn: options.api.create,
    onSuccess: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProviderUpdateBody }) =>
      options.api.update(id, body),
    onSuccess: invalidate,
    onError: () => addToast(settingsCopy.providers.updateFailed, 'error'),
  })

  const deleteMutation = useMutation({
    mutationFn: options.api.delete,
    onSuccess: invalidate,
    onError: () => addToast(settingsCopy.providers.deleteFailed, 'error'),
  })

  return {
    providers,
    isLoading,
    isError,
    onRetry: () => void refetch(),
    onCreate: (body: ProviderFormBody) => createMutation.mutate(body),
    isCreating: createMutation.isPending,
    createError: mutationError(
      createMutation.isError,
      createMutation.error,
      settingsCopy.providers.createFailed,
    ),
    onUpdate: (id: string, body: ProviderUpdateBody) =>
      updateMutation.mutate({ id, body }),
    isUpdating: updateMutation.isPending,
    updateError: mutationError(
      updateMutation.isError,
      updateMutation.error,
      settingsCopy.providers.updateFailed,
    ),
    onDelete: (id: string) => deleteMutation.mutate(id),
    isDeleting: deleteMutation.isPending,
  }
}
